import { useCallback, useEffect, useRef, useState } from 'react'

import { MetricData } from './types'
import { prepareData } from './prepare-data'

declare global {
	interface Window {
		ym?: any
	}
}

interface UseSendParamsProps {
	metricId: number | null
}

const delay = 1500

export const useSendParams = ({ metricId }: UseSendParamsProps) => {
	const paramsToSend = useRef<null | Record<string, MetricData>[]>(null)

	const [queueToSend, setQueueToSend] = useState(false)

	const sendParams = useCallback(
		({ data }: { data?: MetricData[] }) => {
			if (!metricId) return
			window.ym?.(metricId, 'params', data)
		},
		[metricId]
	)

	const addParamsToSet = useCallback(
		({ type, data }: { type: string; data?: MetricData }) => {
			if (!metricId) return
			if (!queueToSend) {
				setQueueToSend(true)
			}
			if (!data) return
			const part = { [type]: prepareData(data) }
			if (paramsToSend.current) {
				paramsToSend.current.push(part)
			} else {
				paramsToSend.current = [part]
			}
		},
		[queueToSend, setQueueToSend, metricId]
	)

	// eslint-disable-next-line no-undef
	const timerId = useRef<NodeJS.Timeout | null>(null)

	useEffect(() => {
		if (!queueToSend || timerId.current || !paramsToSend.current)
			return undefined

		const fn = () => {
			sendParams({ data: paramsToSend.current })
			timerId.current = null
			setQueueToSend(false)
			paramsToSend.current = null
		}

		timerId.current = setTimeout(fn, delay)

		return () => {
			if (timerId.current) {
				clearTimeout(timerId.current)
				fn()
			}
		}
	}, [queueToSend, sendParams])

	return { addParamsToSet }
}
